import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, hotelService } from '../services/api';

export const useHotelSelection = () => {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [error, setError] = useState(null);
  const [isContinuing, setIsContinuing] = useState(false);

  // Memoizar usuário atual
  const currentUser = useMemo(() => {
    try {
      return authService.getCurrentUser();
    } catch {
      return null;
    }
  }, []);

  // Carregar hotéis
  const loadHotels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const availableHotels = await hotelService.getAll();
      
      if (!availableHotels || availableHotels.length === 0) {
        setError('Nenhum hotel disponível para seu usuário');
        return;
      }

      setHotels(availableHotels);
    } catch (error) {
      console.error('Erro ao carregar hotéis:', error);
      setError('Erro ao carregar hotéis. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Selecionar hotel
  const selectHotel = useCallback((hotel) => {
    if (!hotel || !hotel.id || !hotel.name) {
      console.error('Hotel inválido:', hotel);
      return;
    }
    
    setSelectedHotel(hotel);
  }, []);

  // Continuar com hotel selecionado
  const continueWithHotel = useCallback(async () => {
    if (!selectedHotel || isContinuing) return;

    try {
      setIsContinuing(true);
      
      authService.setSelectedHotel(selectedHotel);
      
      // Feedback visual
      await new Promise(resolve => setTimeout(resolve, 300));
      
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Erro ao continuar:', error);
      setError('Erro ao selecionar hotel. Tente novamente.');
    } finally {
      setIsContinuing(false);
    }
  }, [selectedHotel, isContinuing, navigate]);

  // Logout
  const logout = useCallback(() => {
    authService.logout();
    navigate('/login', { replace: true });
  }, [navigate]);

  // Verificar autenticação
  const checkAuth = useCallback(() => {
    if (!currentUser) {
      navigate('/login', { replace: true });
      return false;
    }
    return true;
  }, [currentUser, navigate]);

  return {
    hotels,
    loading,
    selectedHotel,
    error,
    isContinuing,
    currentUser,
    loadHotels,
    selectHotel,
    continueWithHotel,
    logout,
    checkAuth
  };
};
